import { datadogRum } from '@datadog/browser-rum';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { ThemeProvider } from 'styled-components';

import { isPROD } from '@webtrader/core/configs';
import { isRTL, setLanguageChangeRestartCallback } from '@webtrader/core/i18n';
import lazy from '@webtrader/core/lazy';
import { themeService, useTheme } from '@webtrader/core/services/theme/v2';
import storage, { EStorageKey } from '@webtrader/core/storage';
import { EThemeScheme } from '@webtrader/core/themes';

import { GlobalStyle } from '@/components/Base';
import { useInitWebAlert } from '@/services/alert';

const Routes = lazy(() => import('./routes'));

const Screens: FCC = () => {
  useInitWebAlert();
  const { i18n } = useTranslation();

  useEffect(() => {
    themeService.initPc();

    // init language change restart callback
    setLanguageChangeRestartCallback(() => {
      window.location.reload();
    });

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function (e) {
      if (themeService.context.theme.scheme === EThemeScheme.SYSTEM) {
        themeService.switchThemeScheme(e.matches ? EThemeScheme.DARK : EThemeScheme.LIGHT, false);
      }
    });

    document.title = storage.getItem<string>(EStorageKey.TITLE);

    if (isPROD()) {
      // This Datadog function will only run on PROD, the init values will be populated only in PROD builds
      datadogRum.init({
        applicationId: '1e8f71ef-9267-4c55-9fae-b18351df4e53',
        clientToken: 'puba716813be3fcbb30e5606e23af28b739',
        site: 'us5.datadoghq.com',
        service: 'webtrader-web',
        env: 'prod',
        // version: '1.0.0',
        sessionSampleRate: 25,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        trackSessionAcrossSubdomains: true,
        defaultPrivacyLevel: 'mask-user-input'
      });
    }
  }, []);

  useEffect(() => {
    // Set the direction of the page based on the current language
    const direction = i18n.dir();
    document.documentElement.dir = direction; // Set the page direction to "ltr" or "rtl"
  }, [i18n, i18n.language]); // Re-run whenever the language changes

  const { ready, theme } = useTheme();
  const themefontFamily = theme.font.DEFAULT_FONT_FAMILY;
  const fontFamily = `${themefontFamily}, sans-serif`;

  if (!ready) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <StyledWrapper dir={isRTL() ? 'rtl' : 'ltr'}>
        <GlobalStyle $fontFamily={fontFamily} />
        <Routes />
      </StyledWrapper>
    </ThemeProvider>
  );
};

export default Screens;

const StyledWrapper = styled.div`
  flex: 1;
  display: flex;
  height: 100%;
  width: 100%;
`;
